import _ from 'lodash';
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { Redirect } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import momentTimeZone from 'moment-timezone';
import PropTypes from 'prop-types';
import qs from 'qs';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  captureException, ErrorHandler, AUTHENTICATION_SCOPES, withTranslation,
} from '@playbookux/playbook-web-core';

import RescheduleModal from '../../../components/reschedule-modal';

import {
  Container,
  Button,
  BottomContainer,
  ContainerMessage,
  ErrorContainer,
  ErrorHandlerContainer,
  Text,
} from './styles';

import CardSchedule from '../card-schedule';
import {
  TIME_SLOTS, CREATE_SCHEDULED_TESTER, AUDIENCE_CAPACITY,
} from '../graphql';

import {
  setTimeZone,
  setInitialDate,
  setTimeSlotSelected,
  setInitialState,
  setProjectId,
} from '../../../../core/actions';

const NEXT_TEXT_LABEL = 'scheduler_next';
const CONFIRM_TEXT_LABEL = 'scheduler_confirm';
const ALERT_TEXT = 'Please select a time';

const DATE_FORMAT = 'LT';
const FORMAT_24H = 'kk:mm';
const DAY_FORMAT = ' dddd MMM DD';

const ScheduleComponent = (props) => {
  const {
    segment,
    currentProjectId,
    history,
    initialDate,
    location: { search },
    onSetInitialDate,
    onSetInitialState,
    onSetProjectId,
    onSetTimeSlotSelected,
    onSetTimeZone,
    timeSlot,
    timeZone,
    authentication,
    phrases,
  } = props;

  const parsed = qs.parse(search);
  // the parameter guest in the schedule link is to indicate that the link was sent from the admin panel
  const guest = _.get(parsed, 'guest', false) === 'true';

  const FORMAT_DATE_TIME = 'dddd, MMMM Do YYYY, h:mm:ss a';

  const formatDateTime = (time, timezone) => {
    if (!time) return null;

    const newTime = momentTimeZone(time).tz(timezone).format(FORMAT_DATE_TIME);
    return newTime;
  };

  const { segment_id: rawSegmentId, concurrentError } = qs.parse(search, { ignoreQueryPrefix: true });
  const projectId = segment?.project?.id;

  const uuIDRegex = new RegExp(/[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}/i);
  const matchedId = rawSegmentId?.match(uuIDRegex);
  const segmentId = matchedId[0];

  const [timezoneName, setTimezoneName] = useState(null);
  const [toggleTime, setToggleTime] = useState(false);

  const [confirmButtonTouched, setConfirmButtonTouched] = useState(false);
  const errorRef = useRef(null);

  const [originalTestSession, setOriginalTestSession] = useState({});
  const [openModal, setOpenModal] = useState(false);

  const [createScheduledTester, { error: mutationError, loading: mutationLoading }] = useMutation(CREATE_SCHEDULED_TESTER);

  const startAt = momentTimeZone(initialDate).tz(timeZone).startOf('day')
    .toISOString();
  const endAt = momentTimeZone(initialDate).tz(timeZone).add(6, 'days').endOf('day')
    .toISOString();

  const { loading, data, error } = useQuery(TIME_SLOTS, {
    variables: {
      projectId,
      startAt,
      endAt,
    },
    skip: !projectId,
    pollInterval: 3000,
  });

  const { data: dataAudience, loading: loadingAudience } = useQuery(AUDIENCE_CAPACITY, {
    variables: {
      segmentId: segment?.id,
    },
    skip: !segment?.id,
    pollInterval: 3000,
  });

  const {
    device = '',
    project = {},
  } = segment;
  const { timeZone: projectTimezone = null } = project;

  const handleClose = () => {
    setOriginalTestSession({});
    setOpenModal(false);
  };

  const isUnhandledError = (graphqlError) => graphqlError?.message && graphqlError.message !== 'time_slot_not_available'
    && graphqlError.message !== 'has_already_schedule' && graphqlError.message !== 'study_fully_scheduled';

  const handleCreateScheduledTester = useCallback(async (state, forceReschedule) => {
    try {
      if (!forceReschedule && !_.isEmpty(originalTestSession)) {
        const newState = {
          ...state,
          originalTestSession,
          forceReschedule,
        };

        onSetInitialState();
        history.push({
          pathname: '/schedule/summary',
          state: newState,
        });

        return;
      }

      const { data: createScheduleData, error: createScheduleError } = await createScheduledTester({
        variables: {
          startAt: state.date,
          timeZone: state.timeZone,
          projectId: state.projectId,
          segmentId: state.segmentId,
          forceReschedule: forceReschedule || false,
          email: authentication.profile.email,
          firstName: authentication.profile.firstName,
          lastName: authentication.profile.lastName,
        },
      });

      if (!_.isEmpty(createScheduleError)) {
        captureException('SCHEDULE_CONFIRMATION_TESTER_HANDLE_SUBMIT_FAILED', createScheduleError);
      } else {
        history.push({
          pathname: '/schedule/summary',
          state: {
            ...state,
            forceReschedule,
          },
          data: createScheduleData,
        });
      }
    } catch (ex) {
      const errorGraphQL = (ex && ex.graphQLErrors[0].message)
        ? ex.graphQLErrors[0].message
        : 'Error while sending the form';

      if (errorGraphQL === 'study_fully_scheduled') {
        setOpenModal(true);
      }

      if (errorGraphQL === 'time_slot_not_available') {
        onSetInitialState();
        history.push(`/schedule?segment_id=${state.segmentId}&concurrentError=${true}&guest=${guest}`);
      }

      if (errorGraphQL === 'has_already_schedule') {
        setOriginalTestSession({
          startAt: ex.graphQLErrors[0].extensions.start_at,
          endAt: ex.graphQLErrors[0].extensions.end_at,
        });
        setOpenModal(true);
      }
    }
  }, [createScheduledTester, history, onSetInitialState, originalTestSession]);

  const handleClick = (forceReschedule) => {
    setConfirmButtonTouched(false);
    const state = {
      device,
      date: timeSlot,
      timeZone,
      projectId,
      segmentId,
    };

    if (timeSlot && authentication.profile && authentication.scope === AUTHENTICATION_SCOPES.TESTER) {
      handleCreateScheduledTester(state, forceReschedule);
      return;
    }

    setConfirmButtonTouched(true);

    if (!timeSlot) {
      return;
    }

    history.push({
      pathname: '/schedule/confirmation',
      state,
      search: `?guest=${guest}`,
    });
  };

  const timeSlots = data ? data.timeSlots : [];

  const handleTimeZoneChange = (newTimeZone) => {
    onSetTimeZone(newTimeZone || timeZone);
  };

  useEffect(() => {
    onSetTimeZone(moment.tz.guess() || projectTimezone);
  }, [onSetTimeZone, projectTimezone]);

  useEffect(() => {
    if (projectId && currentProjectId && projectId !== currentProjectId) {
      onSetInitialState();
    } else {
      onSetInitialDate();
    }

    if (segment) {
      onSetProjectId(segment.projectId);
    }
  }, [currentProjectId, onSetInitialDate, onSetInitialState, onSetProjectId, projectId, segment]);

  // Reset time slot selected if user refreshes the screen
  const resetTimeSlotSelected = (e) => {
    e.preventDefault();
    onSetTimeSlotSelected(null);
    e.returnValue = '';
  };

  useEffect(() => {
    window.addEventListener('beforeunload', resetTimeSlotSelected);
    return () => {
      window.removeEventListener('beforeunload', resetTimeSlotSelected);
    };
  }, []);

  if (isUnhandledError(mutationError)) {
    return (
      <ErrorHandlerContainer>
        <ErrorHandler
          error={mutationError}
          defaultMessage="Error while confirming schedule."
        />
      </ErrorHandlerContainer>

    );
  }

  if (error || !projectId) {
    return (
      <Redirect to="/" />
    );
  }

  const formatDate = formatDateTime(timeSlot, timeZone);
  const timeFormat = (toggleTime ? FORMAT_24H : DATE_FORMAT) + DAY_FORMAT;
  const ariaLabelConfirmButton = (timeSlot && timeZone)
    ? `${phrases[CONFIRM_TEXT_LABEL]} ${momentTimeZone(timeSlot).tz(timeZone).format(timeFormat)}`
    : phrases[NEXT_TEXT_LABEL];

  return (
    <Container>
      {concurrentError && (
        <ErrorContainer>
          It looks like that time was selected by another participant and is no longer available.
          Please select another time.
        </ErrorContainer>
      )}

      <RescheduleModal
        open={openModal && !_.isEmpty(originalTestSession)}
        loading={mutationLoading}
        onClose={handleClose}
        onSubmit={(value) => handleClick(value)}
        content={`It looks like your email is already signed up for a session with this project
          on ${formatDateTime(originalTestSession.startAt, timeZone)} ${timezoneName}. Would you like to reschedule
          that session to the new time that you’ve just selected (${formatDate} ${timezoneName})?
        `}
      />

      <CardSchedule
        onChangeTimezone={handleTimeZoneChange}
        onTimeChange={onSetTimeSlotSelected}
        timeZone={timeZone}
        selectedTime={timeSlot}
        onSetTimezoneName={setTimezoneName}
        timeSlots={timeSlots}
        onInitialDateChange={onSetInitialDate}
        initialDate={initialDate}
        loading={loading && loadingAudience}
        history={history}
        dataAudience={dataAudience}
        toggleTime={toggleTime}
        setToggleTime={setToggleTime}
        tabIndex="0"
      />

      <BottomContainer>
        <Button
          tabIndex={!confirmButtonTouched || (timeSlot && timeSlot) ? '0' : '-1'}
          onClick={() => handleClick(false)}
          loading={mutationLoading}
          disabled={mutationLoading}
          role="button"
          aria-label={ariaLabelConfirmButton}
        >
          {(timeSlot && timeZone) ? phrases[CONFIRM_TEXT_LABEL] : phrases[NEXT_TEXT_LABEL]}
        </Button>

        <ContainerMessage
          tabIndex={0}
          ref={errorRef}
          hidden={timeSlot || !confirmButtonTouched}
          role="alert"
          aria-label={ALERT_TEXT}
        >
          {ALERT_TEXT}
        </ContainerMessage>
      </BottomContainer>
      <BottomContainer>
        <Text tabIndex="0">
          {phrases.scheduler_nda}
          {' '}
          <a tabIndex="0" href="https://www.playbookux.com/nda/">{phrases.scheduler_nda_2}</a>
        </Text>
      </BottomContainer>
    </Container>
  );
};

ScheduleComponent.propTypes = {
  segment: PropTypes.shape({
    id: PropTypes.string,
    device: PropTypes.string,
    projectId: PropTypes.string,
    project: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      timezone: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  initialDate: PropTypes.shape().isRequired,
  onSetInitialDate: PropTypes.func.isRequired,
  onSetInitialState: PropTypes.func.isRequired,
  onSetTimeSlotSelected: PropTypes.func.isRequired,
  onSetTimeZone: PropTypes.func.isRequired,
  onSetProjectId: PropTypes.func.isRequired,
  timeSlot: PropTypes.string,
  currentProjectId: PropTypes.string,
  timeZone: PropTypes.string.isRequired,
  authentication: PropTypes.shape({
    scope: PropTypes.string.isRequired,
    profile: PropTypes.shape({
      email: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }).isRequired,
  phrases: PropTypes.shape({
    [NEXT_TEXT_LABEL]: PropTypes.string,
    [CONFIRM_TEXT_LABEL]: PropTypes.string,
    scheduler_nda: PropTypes.string,
    scheduler_nda_2: PropTypes.string,
  }).isRequired,
};

ScheduleComponent.defaultProps = {
  timeSlot: undefined,
  currentProjectId: null,
};

const mapStateToProps = ({ schedule, authentication }) => ({
  timeZone: schedule.timeZone,
  initialDate: moment(schedule.initialDate),
  timeSlot: schedule.timeSlot,
  currentProjectId: schedule.projectId,
  authentication,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  onSetTimeZone: setTimeZone,
  onSetInitialDate: setInitialDate,
  onSetTimeSlotSelected: setTimeSlotSelected,
  onSetInitialState: setInitialState,
  onSetProjectId: setProjectId,
}, dispatch);

const enhance = compose(
  withTranslation,
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(ScheduleComponent);
